import React, { useState, useEffect } from 'react';
import SimpleBlogEntry from './SimpleBlogEntry';
import SimpleBlogEntryGrid from './SimpleBlogEntryGrid';
import { Utilities } from '../utils/Utilities';
import { BloggerManager } from '../utils/BloggerManager';
import type { BloggerPost } from '../utils/BloggerManager';
import './SimpleBlogEntryGrid.css';

export type BlogSortField = 'published' | 'updated' | 'title';
export type SortDirection = 'asc' | 'desc';
export type BlogEntriesLayout = 'default' | 'grid';

interface SimpleBlogEntriesProps {
    count: number;
    /** Number of blog posts to skip from the start of the (sorted) result array. Default 0. */
    skip?: number;
    sortOrderField: BlogSortField;
    sortDirection: SortDirection;
    /** Output layout: default (list) or grid (card grid with animation). */
    layout?: BlogEntriesLayout;
    /** Optional list of blog entry titles to show. If not empty, only posts with matching titles are included. */
    blogTitles?: string[];
    /** Optional list of blog IDs to load and show. If not empty, each post is loaded via loadBlogPostById; otherwise items are loaded as before. */
    blogIDs?: string[];
}

function SimpleBlogEntries({
    count,
    skip = 0,
    sortOrderField,
    sortDirection,
    layout = 'default',
    blogTitles,
    blogIDs
}: SimpleBlogEntriesProps) {
    const [posts, setPosts] = useState<BloggerPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                let items: BloggerPost[];

                if (blogIDs && blogIDs.length > 0) {
                    const manager = BloggerManager.getInstance();
                    items = await Promise.all(
                        blogIDs
                            .map((id) => id.trim())
                            .filter(Boolean)
                            .map((id) => manager.loadBlogPostById(id))
                    );
                    count=items.length;
                } else {
                    if (count === 0) { setPosts([]); return;}
                    items = await BloggerManager.getInstance().loadAllBlogPosts();

                    if (blogTitles && blogTitles.length > 0) {
                        const titleSet = new Set(blogTitles.map((t) => t.trim()).filter(Boolean));
                        items = items.filter((post) => titleSet.has(post.title));
                    }
                }

                const sorted = [...items].sort((a, b) => {
                    let cmp: number;
                    switch (sortOrderField) {
                        case 'published':
                            cmp = new Date(a.published).getTime() - new Date(b.published).getTime();
                            break;
                        case 'updated':
                            cmp = new Date(a.updated).getTime() - new Date(b.updated).getTime();
                            break;
                        case 'title':
                            cmp = a.title.localeCompare(b.title);
                            break;
                        default:
                            cmp = new Date(a.published).getTime() - new Date(b.published).getTime();
                    }
                    return sortDirection === 'asc' ? cmp : -cmp;
                });

                setPosts(sorted.slice(skip, skip + count));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };
        fetchPosts();
    }, [count, skip, sortOrderField, sortDirection, blogTitles, blogIDs]);

    if (loading) {
        return (
            <div className="simple-blog-entries">
                <div className="text-center p-4">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="simple-blog-entries">
                <div className="text-center p-4 text-danger">Error: {error}</div>
            </div>
        );
    }

    if (layout === 'grid') {
        return (
            <div className="simple-blog-entries">
                <div className="simple-blog-entry-grid">
                    {posts.map((post) => {
                        const { src: imageUrl, alt: imageAlt } = Utilities.extractImageFromContent(post.content);
                        const tags = post.labels?.length ? post.labels.join(', ') : 'Uncategorized';
                        const date = Utilities.formatDate(post.published);
                        const description = Utilities.extractDescription(post.content);
                        const authorPhoto = post.author?.image?.url ? Utilities.fixImageUrl(post.author.image.url) : undefined;
                        return (
                            <SimpleBlogEntryGrid
                                key={post.id}
                                imageUrl={imageUrl}
                                imageAlt={imageAlt}
                                title={post.title}
                                tags={tags}
                                date={date}
                                description={description}
                                authorName={post.author?.displayName ?? 'Unknown'}
                                authorPhoto={authorPhoto}
                                linkTo={post.url}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="simple-blog-entries">
            {posts.map((post) => {
                const { src: imageUrl, alt: imageAlt } = Utilities.extractImageFromContent(post.content);
                const category = post.labels?.length ? post.labels.join(', ') : 'Uncategorized';
                const date = Utilities.formatDate(post.published);
                return (
                    <SimpleBlogEntry
                        key={post.id}
                        id={parseInt(post.id)}
                        imageUrl={imageUrl}
                        imageAlt={imageAlt}
                        category={category}
                        date={date}
                        title={post.title}
                        linkTo={post.url}
                    />
                );
            })}
        </div>
    );
}

export default SimpleBlogEntries;
